import React from "react";
import { PageHeader } from "../components/PageHeader/PageHeader";

// ─── Types ────────────────────────────────────────────────────────────────────

type ViewType = "list" | "table";

interface Company {
  id: string;
  code: string;
  name: string;
  address: string;
  tags?: string[];
  subCompanies?: Company[];
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_COMPANIES: Company[] = [
  {
    id: "acme-international",
    code: "C-000001",
    name: "Acme International",
    address: "800 W San Antonio St, El Paso, TX 79901, USA",
    tags: ["Maintenance", "Operations"],
    subCompanies: [
      {
        id: "acme-packaging",
        code: "C-000002",
        name: "Acme Packaging",
        address: "220 Harbor Road, Rotterdam, Netherlands",
      },
      {
        id: "acme-logistics",
        code: "C-000003",
        name: "Acme Logistics",
        address: "45 Baker Street, Manchester, UK",
      },
    ],
  },
  {
    id: "nordic-manufacturing",
    code: "C-000004",
    name: "Nordic Manufacturing AB",
    address: "Kungsgatan 12, Stockholm, Sweden",
    tags: ["Operations"],
  },
  {
    id: "baltic-hydraulics",
    code: "C-000005",
    name: "Baltic Hydraulics OY",
    address: "Merikatu 5, Helsinki, Finland",
    tags: ["Maintenance"],
  },
  {
    id: "meridian-electrical",
    code: "C-000006",
    name: "Meridian Electrical Supplies",
    address: "8 Rue de Lyon, Paris, France",
    tags: ["Maintenance", "Operations"],
    subCompanies: [
      {
        id: "meridian-uk",
        code: "C-000007",
        name: "Meridian UK Ltd",
        address: "12 Baker Street, London, UK",
      },
    ],
  },
  {
    id: "continental-lubricants",
    code: "C-000008",
    name: "Continental Lubricants GmbH",
    address: "Werkstrasse 19, Vienna, Austria",
    tags: ["Operations"],
  },
  {
    id: "alpine-safety",
    code: "C-000009",
    name: "Alpine Safety Equipment AG",
    address: "Bahnhofstrasse 3, Zurich, Switzerland",
    tags: ["Maintenance"],
  },
  {
    id: "coca-cola",
    code: "C-000010",
    name: "CocaCola",
    address: "One Coca-Cola Plaza, Atlanta, GA 30313, USA",
    tags: ["Maintenance", "Operations"],
    subCompanies: [
      {
        id: "coca-cola-georgia",
        code: "C-000011",
        name: "CocaCola Georgia",
        address: "One Coca-Cola Plaza, Atlanta, GA 30313, USA",
        subCompanies: [
          {
            id: "coca-cola-atlanta",
            code: "C-000012",
            name: "CocaCola Atlanta",
            address: "191 Peachtree St NE, Atlanta, GA 30303, USA",
          },
          {
            id: "coca-cola-savannah",
            code: "C-000013",
            name: "CocaCola Savannah",
            address: "2 E Bay St, Savannah, GA 31401, USA",
          },
        ],
      },
      {
        id: "coca-cola-california",
        code: "C-000014",
        name: "CocaCola California",
        address: "1000 Sansome St, San Francisco, CA 94111, USA",
        subCompanies: [
          {
            id: "coca-cola-los-angeles",
            code: "C-000015",
            name: "CocaCola Los Angeles",
            address: "1200 S Vermont Ave, Los Angeles, CA 90006, USA",
          },
          {
            id: "coca-cola-san-francisco",
            code: "C-000016",
            name: "CocaCola San Francisco",
            address: "1000 Sansome St, San Francisco, CA 94111, USA",
          },
        ],
      },
      {
        id: "coca-cola-texas",
        code: "C-000017",
        name: "CocaCola Texas",
        address: "6425 United Plaza Blvd, Houston, TX 77057, USA",
        subCompanies: [
          {
            id: "coca-cola-houston",
            code: "C-000018",
            name: "CocaCola Houston",
            address: "6425 United Plaza Blvd, Houston, TX 77057, USA",
          },
          {
            id: "coca-cola-austin",
            code: "C-000019",
            name: "CocaCola Austin",
            address: "301 Congress Ave, Austin, TX 78701, USA",
          },
        ],
      },
      {
        id: "coca-cola-new-york",
        code: "C-000020",
        name: "CocaCola New York",
        address: "711 5th Ave, New York, NY 10022, USA",
        subCompanies: [
          {
            id: "coca-cola-new-york-city",
            code: "C-000021",
            name: "CocaCola New York City",
            address: "711 5th Ave, New York, NY 10022, USA",
          },
          {
            id: "coca-cola-buffalo",
            code: "C-000022",
            name: "CocaCola Buffalo",
            address: "617 Main St, Buffalo, NY 14203, USA",
          },
        ],
      },
    ],
  },
];

// ─── Icons ────────────────────────────────────────────────────────────────────

function CompanyIcon() {
  return (
    <div style={{
      width: 40, height: 40, borderRadius: 10, flexShrink: 0,
      background: "#e6e8fe",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#0517f8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 17V4.5C4 3.67157 4.67157 3 5.5 3H11.5C12.3284 3 13 3.67157 13 4.5V17" />
        <path d="M13 8.5H15.5C16.3284 8.5 17 9.17157 17 10V17" />
        <path d="M6.5 6.5H9" />
        <path d="M6.5 9.5H9" />
        <path d="M6.5 12.5H9" />
        <path d="M2.5 17H17.5" />
      </svg>
    </div>
  );
}

function ChevronUpIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 10l4-4 4 4" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

function DotsMoreIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="#748094">
      <circle cx="4.5" cy="10" r="1.5" />
      <circle cx="10" cy="10" r="1.5" />
      <circle cx="15.5" cy="10" r="1.5" />
    </svg>
  );
}

function ListViewIcon({ active }: { active: boolean }) {
  const c = active ? "#0517f8" : "#a2aab8";
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" strokeLinecap="round">
      <rect x="2" y="3" width="2" height="2" rx="0.5" fill={c} />
      <path d="M6.5 4h7.5" stroke={c} strokeWidth="1.4" />
      <rect x="2" y="7" width="2" height="2" rx="0.5" fill={c} />
      <path d="M6.5 8h7.5" stroke={c} strokeWidth="1.4" />
      <rect x="2" y="11" width="2" height="2" rx="0.5" fill={c} />
      <path d="M6.5 12h7.5" stroke={c} strokeWidth="1.4" />
    </svg>
  );
}

function TableViewIcon({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={active ? "#0517f8" : "#a2aab8"} strokeWidth="1.4" strokeLinecap="round">
      <rect x="2" y="2" width="12" height="12" rx="1.5" />
      <path d="M2 6h12M6 6v8" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4h12M4 8h8M6 12h4" />
    </svg>
  );
}

function ImportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2v8M5 7l3 3 3-3" />
      <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" />
    </svg>
  );
}

// ─── Company Card ─────────────────────────────────────────────────────────────

interface CompanyCardProps {
  company: Company;
  hasChildren: boolean;
  expanded: boolean;
  onToggle: () => void;
  onOpen?: (id: string, name: string) => void;
}

function CompanyCard({ company, hasChildren, expanded, onToggle, onOpen }: CompanyCardProps) {
  const subCount = company.subCompanies?.length ?? 0;
  const [hovered, setHovered] = React.useState(false);
  const visibleTag = company.tags?.[0];
  const extraTagCount = (company.tags?.length ?? 0) - 1;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpen?.(company.id, company.name)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: 16,
        background: hovered ? "var(--color-bg-accent)" : "white",
        border: "1px solid #e8eaed",
        borderRadius: 8,
        width: "100%",
        boxSizing: "border-box",
        transition: "background 80ms ease",
        cursor: onOpen ? "pointer" : "default",
      }}
    >
      {/* Left: chevron + icon + info */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
        {hasChildren && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            style={{
              width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center",
              background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0,
            }}
          >
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </button>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minWidth: 0 }}>
          <CompanyIcon />

          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              lineHeight: "24px",
              letterSpacing: "-0.32px",
              color: "#172b4d",
              fontFamily: "var(--font-family-base)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}>
              {company.name}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "nowrap" }}>
              <span style={{ fontSize: 12, fontWeight: 500, lineHeight: "16px", color: "#748094", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>
                {company.code}
              </span>
              {subCount > 0 && (
                <>
                  <span style={{ color: "#a2aab8", fontSize: 12, lineHeight: "16px" }}>•</span>
                  <span style={{ fontSize: 12, fontWeight: 500, lineHeight: "16px", color: "#748094", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>
                    {subCount} Sub {subCount === 1 ? "Company" : "Companies"}
                  </span>
                </>
              )}
              {company.address && (
                <>
                  <span style={{ color: "#a2aab8", fontSize: 12, lineHeight: "16px" }}>•</span>
                  <span style={{
                    fontSize: 12, fontWeight: 500, lineHeight: "16px", color: "#748094",
                    fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
                    overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {company.address}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right: tags + more */}
      {visibleTag && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            padding: "4px 6px", borderRadius: 4, background: "#f3f4f6",
            fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
            color: "#748094", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
          }}>
            {visibleTag}
          </span>
          {extraTagCount > 0 && (
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              padding: "4px 6px", borderRadius: 4, background: "#f3f4f6",
              fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
              color: "#748094", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
            }}>
              +{extraTagCount}
            </span>
          )}
        </div>
      )}

      <button
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 20, height: 20, background: "none", border: "none",
          cursor: "pointer", padding: 0, flexShrink: 0,
        }}
      >
        <DotsMoreIcon />
      </button>
    </div>
  );
}

// ─── Company Row (recursive, mirrors AssetsListPage's tree pattern) ───────────

interface CompanyRowProps {
  company: Company;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  onOpen?: (id: string, name: string) => void;
}

function CompanyRow({ company, expandedIds, onToggle, onOpen }: CompanyRowProps) {
  const hasChildren = !!(company.subCompanies && company.subCompanies.length > 0);
  const isExpanded = expandedIds.has(company.id);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <CompanyCard
        company={company}
        hasChildren={hasChildren}
        expanded={isExpanded}
        onToggle={() => onToggle(company.id)}
        onOpen={onOpen}
      />
      {hasChildren && isExpanded && (
        <div style={{ display: "flex", alignItems: "stretch", gap: 0 }}>
          <div style={{ width: 48, flexShrink: 0, position: "relative" }}>
            <div style={{ position: "absolute", left: 23, top: 0, bottom: 0, width: 1, background: "#e8eaed" }} />
          </div>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {company.subCompanies!.map(sub => (
              <CompanyRow key={sub.id} company={sub} expandedIds={expandedIds} onToggle={onToggle} onOpen={onOpen} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const VIEW_OPTIONS: { key: ViewType; label: string; Icon: React.FC<{ active: boolean }> }[] = [
  { key: "list", label: "List", Icon: ListViewIcon },
  { key: "table", label: "Table", Icon: TableViewIcon },
];

export interface CompaniesListPageProps {
  onOpenCompany?: (companyId: string, companyName: string) => void;
  onCreateCompany?: () => void;
}

export function CompaniesListPage({ onOpenCompany, onCreateCompany }: CompaniesListPageProps) {
  const [view, setView] = React.useState<ViewType>("list");
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set(["acme-international"]));

  function toggleExpanded(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div style={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      background: "var(--color-bg-primary)",
    }}>
      <PageHeader
        breadcrumbs={[{ label: "Companies" }]}
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              aria-label="Search"
              style={{ width: 40, height: 40, background: "var(--color-bg-accent)", border: "none", borderRadius: "var(--radius-s)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667Z" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14 14l-2.9-2.9" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              aria-label="Import"
              style={{ width: 40, height: 40, background: "var(--color-bg-accent)", border: "none", borderRadius: "var(--radius-s)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            >
              <ImportIcon />
            </button>
            <button
              onClick={onCreateCompany}
              style={{ height: 40, padding: "0 16px", background: "var(--color-bg-brand-dark)", border: "none", borderRadius: "var(--radius-s)", fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px", color: "var(--color-content-inverse)", cursor: "pointer", whiteSpace: "nowrap" }}
            >
              New Company
            </button>
          </div>
        }
      />

      {/* Toolbar: view tabs + filters */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px var(--spacing-2xl)",
        borderBottom: "1px solid var(--color-border-primary)",
        background: "var(--color-bg-primary)",
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {VIEW_OPTIONS.map(({ key, label, Icon }) => {
            const active = view === key;
            return (
              <button
                key={key}
                onClick={() => setView(key)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "6px 8px", borderRadius: 4,
                  border: "none", cursor: "pointer",
                  background: active ? "#e6e8fe" : "transparent",
                  fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
                  color: active ? "var(--color-content-primary)" : "#a2aab8",
                  fontFamily: "var(--font-family-base)", transition: "all 100ms",
                }}
              >
                <Icon active={active} />
                {label}
              </button>
            );
          })}
        </div>

        <button style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "8px 12px",
          border: "1px solid var(--color-border-primary)",
          borderRadius: 8, background: "var(--color-bg-primary)",
          fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
          color: "var(--color-content-primary)",
          fontFamily: "var(--font-family-base)", cursor: "pointer", flexShrink: 0,
        }}>
          <FilterIcon />
          Filters
        </button>
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "var(--spacing-l) var(--spacing-2xl)",
        background: "var(--color-bg-primary)",
      }}>
        {view === "list" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {MOCK_COMPANIES.map(company => (
              <CompanyRow
                key={company.id}
                company={company}
                expandedIds={expandedIds}
                onToggle={toggleExpanded}
                onOpen={onOpenCompany}
              />
            ))}
          </div>
        )}

        {view === "table" && (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: 8, minHeight: 400, color: "#748094", fontFamily: "var(--font-family-base)",
          }}>
            <span style={{ fontSize: 32 }}>🚧</span>
            <p style={{ fontSize: 14, fontWeight: 700, color: "#172b4d", margin: 0 }}>Table View</p>
            <p style={{ fontSize: 13, fontWeight: 500, color: "#748094", margin: 0 }}>This view is coming soon.</p>
          </div>
        )}
      </div>
    </div>
  );
}
